import { ProfileAPIService, FotaAPIService, DataAPIService } from './AxiosAPIService';
import { logger } from './SharedService';
import {
  Device, DeviceDetails,
  DevicesResponse,
  DeviceStatusResponse,
  DeviceUpgradesResponse,
  DeviceInstallResponse,
  DeviceInstallPayload,
  AlertsConfiguration,
  AlertsConfigurationRequest,
  DeviceConnectivityResponse,
  DeviceConfiguration,
  DeviceConfigurationPatch,
  DeviceFleetConfiguration,
  DeviceFleetConfigurationPatch,
  FleetConfigStatusResponse,
  DeviceConfigurationReset,
  DeviceConfigurationResetResult,
  DeviceDefaultConfiguration,
  DeviceDefaultConfigFile,
  DeviceTestConfiguration,
  DeviceTestConfigurationPost,
} from '../types/Devices';

const DEVICES_ENDPOINT = '/fleets';

export const getDevices = async (fleetId: string, query: string): Promise<DevicesResponse> => {
  const { data } = await ProfileAPIService.api
    .get<DevicesResponse>(`${DEVICES_ENDPOINT}/${fleetId}/devices${query}`);
  return data;
};

export const getDeviceById = async (id: string, fleetId: string): Promise<DeviceDetails> => {
  const { data } = await ProfileAPIService.api
    .get<DeviceDetails>(`${DEVICES_ENDPOINT}/${fleetId}/devices/${id}`);
  return data;
};

export const getDeviceByDeviceId = async (id: string, fleetId: string): Promise<Device> => {
  const { data } = await ProfileAPIService.api
    .get<Device>(`${DEVICES_ENDPOINT}/${fleetId}/devices/deviceId/${id}`);
  return data;
};

export const getDeviceStatus = async (fleetId: string, query = ''): Promise<DeviceStatusResponse> => {
  const { data } = await ProfileAPIService.api
    .get<DeviceStatusResponse>(`${DEVICES_ENDPOINT}/${fleetId}/devices/aggregation${query}`);
  return data;
};

/** Response from GET /fleet/:fleetId/device/:deviceId (Profile API) */
export interface DeviceOnlineStatusResponse {
  isOnline: boolean;
}

export const getDeviceOnlineStatus = async (
  fleetId: string,
  deviceId: string,
): Promise<DeviceOnlineStatusResponse> => {
  const path = `/fleet/${fleetId}/device/${encodeURIComponent(deviceId)}`;
  const { data } = await ProfileAPIService.api
    .get<DeviceOnlineStatusResponse>(path)
    .catch((e) => Promise.reject(e.response?.data ?? e));
  return data ?? { isOnline: false };
};

export const getDeviceAvailableUpgrades = async (
  id: string,
): Promise<DeviceUpgradesResponse> => {
  const { data } = await FotaAPIService.api
    .get<DeviceUpgradesResponse>(`/firmware/available-upgrades/${id}`);
  return data;
};

export const postDeviceUpgrade = async ({
  firmwareId,
  deviceId,
}: DeviceInstallPayload): Promise<DeviceInstallResponse> => {
  const { data } = await FotaAPIService.api
    .post<DeviceInstallResponse>('device-firmware/install', {
    firmwareId,
    deviceId,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const getDeviceAlertSettingsByDeviceId = async (
  deviceId: string,
  fleetId: string,
  alertName: string,
): Promise<AlertsConfiguration> => {
  logger.info('GET', 'REQUEST', 'getDeviceAlertSettingsByDeviceId', `${fleetId}/devices/${deviceId}/alerts-config/${alertName}`);
  const { data } = await DataAPIService.api
    .get<AlertsConfiguration>(`${fleetId}/devices/${deviceId}/alerts-config/${alertName}`)
    .catch((e) => Promise.reject(e.response.data));
  logger.info('GET', 'RESPONSE', 'getDeviceAlertSettingsByDeviceId', data);
  return data;
};

export const getGlobalAlertSettingsByFleetId = async (
  fleetId: string,
  alertName: string,
): Promise<AlertsConfiguration> => {
  logger.info('GET', 'REQUEST', 'getGlobalAlertSettingsByFleetId', `${fleetId}/alerts-config/${alertName}`);
  const { data } = await DataAPIService.api
    .get<AlertsConfiguration>(`${fleetId}/alerts-config/${alertName}`)
    .catch((e) => Promise.reject(e.response.data));
  logger.info('GET', 'RESPONSE', 'getGlobalAlertSettingsByFleetId', data);
  return data;
};

export const postDeviceAlertSettingsByDeviceId = async ({
  fleetId,
  deviceId,
  alertName,
  payload,
}: AlertsConfigurationRequest): Promise<AlertsConfiguration> => {
  logger.info('POST', 'REQUEST', 'postDeviceAlertSettingsByDeviceId', `${fleetId}/alerts-config/${alertName}`, deviceId, alertName, payload);
  // if (['DevStreamDriverNotFound', 'DevStreamEventHighG', 'DevStreamEventCameraBlockageInterior', 'DevStreamEventCameraBlockageExterior', 'DrivingOvertime', 'DriverUnauthorized', 'DriverUnknown'].includes(alertName)) {
  //   payload.sound = false;
  // }
  const { data } = await DataAPIService.api
    .post<AlertsConfiguration>(`${fleetId}/devices/${deviceId}/alerts-config`, {
    alertName,
    ...payload,
  })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('POST', 'RESPONSE', 'postDeviceAlertSettingsByDeviceId', data);
  return data;
};

export const postGlobalAlertSettingsByFleetId = async ({
  fleetId,
  alertName,
  payload,
}: AlertsConfigurationRequest): Promise<AlertsConfiguration> => {
  logger.info('POST', 'REQUEST', 'postGlobalAlertSettingsByFleetId', fleetId, alertName, payload);
  // if (['DevStreamDriverNotFound', 'DevStreamEventHighG', 'DevStreamEventCameraBlockageInterior', 'DevStreamEventCameraBlockageExterior', 'DrivingOvertime', 'DriverUnauthorized', 'DriverUnknown'].includes(alertName)) {
  //   payload.sound = false;
  // }
  const { data } = await DataAPIService.api
    .post<AlertsConfiguration>(`${fleetId}/alerts-config`, {
    alertName,
    ...payload,
  })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('POST', 'RESPONSE', 'postGlobalAlertSettingsByFleetId', data);
  return data;
};

export const getDevicesConnectivity = async (
  fleetId: string,
  query?: string,
): Promise<DeviceConnectivityResponse> => {
  logger.info('GET', 'REQUEST', 'getDevicesConnectivity', `${DEVICES_ENDPOINT}/${fleetId}/devices-connectivity${query}`);
  const { data } = await ProfileAPIService.api
    .get<DeviceConnectivityResponse>(`${DEVICES_ENDPOINT}/${fleetId}/devices-connectivity${query}`);
  logger.info('GET', 'RESPONSE', 'getDevicesConnectivity', data);
  return data;
};

// ==========================================================================
// new logic for device configuration
// ==========================================================================

// get device configuration file
export const getDeviceConfiguration = async (
  id: string,
  fleetId: string,
): Promise<DeviceConfiguration> => {
  logger.info('GET', 'REQUEST', 'getDeviceConfiguration', `/device-config/fleets/${fleetId}/devices/${id}`);
  const { data } = await FotaAPIService.api
    .get<DeviceConfiguration>(`/device-config/fleets/${fleetId}/devices/${id}`);
  logger.info('GET', 'RESPONSE', 'getDeviceConfiguration', data);
  return data;
};

// get fleet configuration status
export const getFleetConfigStatus = async (
  fleetId: string,
): Promise<FleetConfigStatusResponse> => {
  logger.info('GET', 'REQUEST', 'getFleetConfigStatus', `/device-config/fleets/${fleetId}`);
  const { data } = await FotaAPIService.api
    .get<FleetConfigStatusResponse>(`/device-config/fleets/${fleetId}`)
    .catch((e) => Promise.reject(e.response?.data));
  logger.info('GET', 'RESPONSE', 'getFleetConfigStatus', data);
  return data;
};

// patch device configuration file
export const patchDeviceConfiguration = async (params: DeviceConfigurationPatch): Promise<DeviceConfiguration> => {
  logger.info('PATCH', 'REQUEST', 'patchDeviceConfiguration', `/device-config/fleets/${params.fleetId}/devices/${params.deviceId}`);
  const { data } = await FotaAPIService.api
    .patch<DeviceConfiguration>(`/device-config/fleets/${params.fleetId}/devices/${params.deviceId}`, {
    configuration: params.configuration,
  })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('PATCH', 'RESPONSE', 'patchDeviceConfiguration', data);
  return data;
};

// The built-in fallback value of every device parameter, straight from the FOTA parameter
// registry. Used to seed this form and to label each field's default — note "Reset to Default"
// does not write these back, it writes the device's uploaded config file.
export const getDeviceDefaultConfiguration = async (): Promise<DeviceDefaultConfiguration> => {
  logger.info('GET', 'REQUEST', 'getDeviceDefaultConfiguration', '/device-config/defaults');
  const { data } = await FotaAPIService.api
    .get<DeviceDefaultConfiguration>('/device-config/defaults')
    .catch((e) => Promise.reject(e.response?.data ?? e));
  logger.info('GET', 'RESPONSE', 'getDeviceDefaultConfiguration', data);
  return data;
};

// The defaults that apply to this one device — the config file uploaded for its hwModel +
// fwVersion pair, which is exactly what postDeviceConfigurationResetDefault writes back. Used to
// label each field with the value it would revert to. Reports available: false rather than
// failing when no file covers the device, so the form still renders.
export const getDeviceDefaultConfigFile = async (
  fleetId: string,
  deviceId: string,
): Promise<DeviceDefaultConfigFile> => {
  const path = `/device-config/fleets/${fleetId}/devices/${deviceId}/defaults`;
  logger.info('GET', 'REQUEST', 'getDeviceDefaultConfigFile', path);
  const { data } = await FotaAPIService.api
    .get<DeviceDefaultConfigFile>(path)
    .catch((e) => Promise.reject(e?.response?.data ?? e));
  logger.info('GET', 'RESPONSE', 'getDeviceDefaultConfigFile', data);
  return data;
};

// Reset device configuration to the config file uploaded for the device's exact hwModel +
// fwVersion pair (the FOTA deviceConfigPreset records, managed from the admin portal). There is
// no fallback: when no such file has been uploaded the request fails with a 404 whose message
// names the missing pair, and that message is what the caller shows the user.
export const postDeviceConfigurationResetDefault = async (
  { fleetId, deviceId }: DeviceConfigurationReset,
): Promise<DeviceConfigurationResetResult> => {
  const path = `/device-config/fleets/${fleetId}/devices/${deviceId}/reset-default`;
  logger.info('POST', 'REQUEST', 'postDeviceConfigurationResetDefault', path);
  // resetCalibration stays false — the client portal must never reset per-installation calibration.
  // Sent explicitly rather than left to the endpoint default, so the intent is visible in the request.
  const { data } = await FotaAPIService.api
    .post<DeviceConfigurationResetResult>(path, { resetCalibration: false })
    .catch((e) => Promise.reject(e.response?.data ?? e));
  logger.info('POST', 'RESPONSE', 'postDeviceConfigurationResetDefault', data);
  return data;
};

// get the test config state of a device
export const getDeviceTestConfiguration = async (
  deviceId: string,
  fleetId: string,
): Promise<DeviceTestConfiguration> => {
  const path = `/device-config/fleets/${fleetId}/devices/${deviceId}/test-config`;
  logger.info('GET', 'REQUEST', 'getDeviceTestConfiguration', path);
  const { data } = await FotaAPIService.api
    .get<DeviceTestConfiguration>(path);
  logger.info('GET', 'RESPONSE', 'getDeviceTestConfiguration', data);
  return data;
};

// enable/disable the test config of a device
export const postDeviceTestConfiguration = async (
  { fleetId, deviceId, enabled }: DeviceTestConfigurationPost,
): Promise<DeviceTestConfiguration> => {
  const path = `/device-config/fleets/${fleetId}/devices/${deviceId}/test-config`;
  logger.info('POST', 'REQUEST', 'postDeviceTestConfiguration', path, enabled);
  const { data } = await FotaAPIService.api
    .post<DeviceTestConfiguration>(path, { enabled })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('POST', 'RESPONSE', 'postDeviceTestConfiguration', data);
  return data;
};

// get device configuration file per fleet
export const getDeviceConfigurationPerFleet = async (
  fleetId: string,
): Promise<DeviceFleetConfiguration> => {
  logger.info('GET', 'REQUEST', 'getDeviceConfigurationPerFleet', `/device-config/fleets/${fleetId}/default`);
  const { data } = await FotaAPIService.api
    .get<DeviceFleetConfiguration>(`/device-config/fleets/${fleetId}/default`);
  logger.info('GET', 'RESPONSE', 'getDeviceConfigurationPerFleet', data);
  return data;
};

// patch device configuration file per fleet
export const patchDeviceConfigurationPerFleet = async (params: DeviceFleetConfigurationPatch): Promise<DeviceFleetConfiguration> => {
  logger.info('PATCH', 'REQUEST', 'patchDeviceConfigurationPerFleet', `/device-config/fleets/${params.fleetId}/default`);
  const { data } = await FotaAPIService.api
    .patch<DeviceFleetConfiguration>(`/device-config/fleets/${params.fleetId}/default`, {
    configuration: params.configuration,
    isActive: params.isActive,
  })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('PATCH', 'RESPONSE', 'patchDeviceConfigurationPerFleet', data);
  return data;
};
